/**
 * TcVersionGuardRoute.tsx — React route wrapper that enforces T&C version check.
 *
 * Story 7.7: Provider T&C Agreement System — Onboarding Hard Gate.
 *
 * AC: 1, 4 — redirects to /terms-agreement if provider's accepted T&C version
 * is behind the current active document version.
 *
 * Wraps all authenticated provider routes except /terms-agreement and /login.
 * The check is client-side gating — the server-side TcVersionGuard provides
 * the authoritative enforcement (defense in depth).
 *
 * Uses GET /v1/providers/tc/my-status (provider-accessible) instead of
 * GET /v1/providers/:id (staff-only) to avoid a 403 redirect loop.
 */

import { usePortalAuth } from '@nrx/auth/react';
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

type GuardState = 'loading' | 'pass' | 'redirect';

interface TcMyStatus {
  isUpToDate: boolean;
  currentDocumentVersion: string | null;
  myAcceptedVersion: string | null;
  providerId: string | null;
}

export function TcVersionGuardRoute(): JSX.Element {
  const auth = usePortalAuth();
  const [guardState, setGuardState] = useState<GuardState>('loading');

  useEffect(() => {
    if (!auth.isAuthenticated) {
      // Not authenticated — RequireAuth above us in the tree handles the redirect.
      setGuardState('pass');
      return;
    }

    let cancelled = false;

    async function checkTcVersion() {
      try {
        // Single endpoint resolves both current doc version and provider's accepted version.
        // Whitelisted in TcVersionGuard so it is reachable even when T&C is behind.
        const res = await fetch('/v1/providers/tc/my-status', {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });

        if (!res.ok) {
          // Fail-closed: any error redirects to /terms-agreement rather than
          // silently opening the gate on a server error or auth failure.
          if (!cancelled) setGuardState('redirect');
          return;
        }

        const status = (await res.json()) as TcMyStatus;

        if (!cancelled) {
          setGuardState(status.isUpToDate ? 'pass' : 'redirect');
        }
      } catch {
        // Network error — fail-closed.
        if (!cancelled) setGuardState('redirect');
      }
    }

    void checkTcVersion();
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated]);

  if (guardState === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-gray-500">Checking access…</p>
      </div>
    );
  }

  if (guardState === 'redirect') {
    return <Navigate to="/terms-agreement" replace />;
  }

  return <Outlet />;
}
